/**
 * Lesson Service
 *
 * Centralized service for all lesson-related API calls, following the project's API standards.
 * This service leverages the buildApiPath utility to ensure consistent URL construction.
 */
import { supabase } from '../utils/supabase-client';
import axiosInstance from '../utils/axios-auth';
// Helper function to extract main content from various content formats
const extractMainContent = (content) => {
    if (!content)
        return '';
    // If content is already a string, return it
    if (typeof content === 'string') {
        return content;
    }
    // If content is an object (legacy JSONB structure), extract main content
    if (typeof content === 'object') {
        // Try to extract from lesson_materials.main_content
        if (content.lesson_materials?.main_content) {
            return content.lesson_materials.main_content;
        }
        // Try to extract from objectives array
        if (content.objectives && Array.isArray(content.objectives)) {
            return content.objectives.join(' ');
        }
        // Fallback: stringify the object
        return JSON.stringify(content);
    }
    return '';
};
// Helper function to convert legacy JSONB content to new TEXT format
const convertLegacyContent = (legacyContent) => {
    if (!legacyContent)
        return '';
    // If it's already a string, return as is
    if (typeof legacyContent === 'string') {
        return legacyContent;
    }
    // If it's an object, try to extract the main content
    if (typeof legacyContent === 'object') {
        // Try to get main_content from lesson_materials
        if (legacyContent.lesson_materials?.main_content) {
            return legacyContent.lesson_materials.main_content;
        }
        // Try to get objectives
        if (legacyContent.objectives && Array.isArray(legacyContent.objectives)) {
            return legacyContent.objectives.join('\n');
        }
        // Fallback: stringify the object
        return JSON.stringify(legacyContent);
    }
    return '';
};
export const lessonService = {
    // Create a new lesson
    async createLesson(lesson) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Convert content to TEXT format
            let content = lesson.content || '';
            if (typeof content === 'object') {
                content = convertLegacyContent(content);
            }
            else if (typeof content !== 'string') {
                content = String(content);
            }
            // Extract main content for search
            const mainContent = extractMainContent(content);
            // Debug logging for lesson creation
            // Use the RPC function for secure lesson creation
            const { data, error } = await supabase.rpc('create_lesson_secure', {
                p_user_id: user.id,
                p_title: lesson.title || '',
                p_description: lesson.description || '',
                p_content: content,
                p_main_content: mainContent,
                p_subject_id: lesson.subject_id || null,
                p_subcategory_id: lesson.subcategory_id || null,
                p_education_system_id: lesson.education_system_id || null,
                p_academic_level_id: lesson.academic_level_id || null,
                p_status: lesson.status || 'draft',
                p_difficulty: lesson.difficulty || 'intermediate',
                p_duration: lesson.duration || 60,
                p_estimated_study_time: lesson.estimated_study_time || 30,
                p_prerequisites: lesson.prerequisites || null,
                p_learning_objectives: lesson.learning_objectives || null,
                p_tags: lesson.tags || null,
                p_is_featured: lesson.is_featured || false,
                p_file_url: lesson.file_url || null,
                p_file_mime_type: lesson.file_mime_type || null,
                p_file_original_name: lesson.file_original_name || null,
                p_file_size: lesson.file_size || null,
                p_file_metadata: lesson.file_metadata || {},
                p_metadata: lesson.metadata || {}
            });
            if (error) {
                console.error('Error from RPC call:', error);
                throw error;
            }
            if (!data) {
                console.error('No data returned from RPC call');
                throw new Error('No lesson data returned from server');
            }
            // Debug logging for returned lesson data
            // Auto-associate lesson with classroom if created in classroom context
            if (data?.id && lesson.metadata?.classroom_id) {
                try {
                    const { data: associationResult, error: associationError } = await supabase.rpc('auto_associate_lesson_with_classroom', {
                        p_lesson_id: data.id,
                        p_created_by: user.id
                    });
                    if (associationError) {
                        console.error('Error auto-associating lesson with classroom:', associationError);
                    }
                    else if (associationResult?.success) {
                    }
                }
                catch (associationError) {
                    console.error('Error in auto-association process:', associationError);
                }
            }
            // Validate the returned data structure
            if (!data || typeof data !== 'object') {
                console.error('Invalid lesson data returned:', data);
                throw new Error('Invalid lesson data returned from server');
            }
            if (!data.id) {
                console.error('Lesson data missing ID field:', data);
                throw new Error('Lesson data missing required ID field');
            }
            return data;
        }
        catch (error) {
            console.error('Error creating lesson:', error);
            throw error;
        }
    },
    // Get a lesson by ID
    async getLessonById(id) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                console.error('LessonService: User not authenticated');
                throw new Error('User not authenticated');
            }
            // Try RPC function first
            try {
                const { data, error } = await supabase.rpc('get_lesson_by_id_secure', {
                    p_user_id: user.id,
                    p_lesson_id: id
                });
                if (error) {
                    console.error('LessonService: RPC error:', error);
                    throw error;
                }
                return data;
            }
            catch (rpcError) {
                console.error('LessonService: RPC failed, trying direct query:', rpcError);
                // Fallback to direct query if RPC fails
                const { data, error } = await supabase
                    .from('lessons')
                    .select('*')
                    .eq('id', id)
                    .eq('created_by', user.id)
                    .single();
                if (error) {
                    console.error('LessonService: Direct query error:', error);
                    throw error;
                }
                return data;
            }
        }
        catch (error) {
            console.error('LessonService: Error fetching lesson:', error);
            return null;
        }
    },
    // Debug function to check lesson table structure
    async debugLessonTable() {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Check if lessons table exists and get its structure
            const { data: tableInfo, error: tableError } = await supabase
                .from('lessons')
                .select('*')
                .limit(1);
            if (tableError) {
                console.error('LessonService: Table structure error:', tableError);
                return { error: tableError };
            }
            // Try to get user's lessons
            const { data: userLessons, error: userError } = await supabase
                .from('lessons')
                .select('*')
                .eq('created_by', user.id)
                .limit(5);
            if (userError) {
                console.error('LessonService: User lessons query error:', userError);
                return { error: userError };
            }
            return { tableExists: true, userLessons: userLessons?.length || 0 };
        }
        catch (error) {
            console.error('LessonService: Debug error:', error);
            return { error };
        }
    },
    // Update a lesson
    async updateLesson(id, updates) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Convert content to TEXT format if provided
            let content = updates.content;
            if (content !== undefined) {
                if (typeof content === 'object') {
                    content = convertLegacyContent(content);
                }
                else if (typeof content !== 'string') {
                    content = String(content);
                }
            }
            // Extract main content for search if content is being updated
            let mainContent = undefined;
            if (content !== undefined) {
                mainContent = extractMainContent(content);
            }
            // Use the RPC function for secure lesson update.
            // Pass only the params we have; the single update_lesson_secure function
            // accepts all as optional (DEFAULT NULL).
            const { data, error } = await supabase.rpc('update_lesson_secure', {
                p_user_id: user.id,
                p_lesson_id: id,
                p_title: updates.title ?? null,
                p_description: updates.description ?? null,
                p_content: content ?? null,
                p_main_content: mainContent ?? null,
                p_subject_id: updates.subject_id ?? null,
                p_subcategory_id: updates.subcategory_id ?? null,
                p_status: updates.status ?? null,
                p_difficulty: updates.difficulty ?? null,
                p_duration: updates.duration ?? null,
                p_estimated_study_time: updates.estimated_study_time ?? null,
                p_prerequisites: updates.prerequisites ?? null,
                p_learning_objectives: updates.learning_objectives ?? null,
                p_tags: updates.tags ?? null,
                p_is_featured: updates.is_featured ?? null,
                p_file_url: updates.file_url ?? null,
                p_file_mime_type: updates.file_mime_type ?? null,
                p_file_original_name: updates.file_original_name ?? null,
                p_file_size: updates.file_size ?? null,
                p_file_metadata: updates.file_metadata ?? null,
                p_metadata: updates.metadata ?? null
            });
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error updating lesson:', error);
            throw error;
        }
    },
    // Delete a lesson and all associated files
    async deleteLesson(id) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Use the comprehensive deletion API with axiosInstance
            const response = await axiosInstance.delete(`/lessons/${id}`);
            return {
                success: response.data.success,
                message: response.data.message,
                deletedFiles: response.data.deletedFiles || []
            };
        }
        catch (error) {
            console.error('Error deleting lesson:', error);
            throw error;
        }
    },
    // Get all lessons with filters
    async getAllLessons(filters) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Build filters object for RPC
            const rpcFilters = {};
            if (filters?.subject_id)
                rpcFilters.subject_id = filters.subject_id;
            if (filters?.status)
                rpcFilters.status = filters.status;
            if (filters?.difficulty)
                rpcFilters.difficulty = filters.difficulty;
            // Use the RPC function for secure lesson retrieval
            const { data, error } = await supabase.rpc('get_lessons_secure', {
                p_user_id: user.id,
                p_filters: rpcFilters,
                p_limit: 100,
                p_offset: 0
            });
            if (error)
                throw error;
            return data.lessons || [];
        }
        catch (error) {
            console.error('Error fetching lessons:', error);
            return [];
        }
    },
    // Get lessons by tutor
    async getLessonsByTutor(tutorId) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Use the RPC function with filters
            const { data, error } = await supabase.rpc('get_lessons_secure', {
                p_user_id: user.id,
                p_filters: { created_by: tutorId },
                p_limit: 100,
                p_offset: 0
            });
            if (error)
                throw error;
            return data.lessons || [];
        }
        catch (error) {
            console.error('Error fetching tutor lessons:', error);
            return [];
        }
    },
    // Search lessons
    async searchLessons(query) {
        try {
            // Use the search function
            const { data, error } = await supabase.rpc('search_lessons_simple', {
                search_query: query
            });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error searching lessons:', error);
            return [];
        }
    },
    // Get published lessons
    async getPublishedLessons() {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Use the RPC function with status filter
            const { data, error } = await supabase.rpc('get_lessons_secure', {
                p_user_id: user.id,
                p_filters: { status: 'published' },
                p_limit: 100,
                p_offset: 0
            });
            if (error)
                throw error;
            return data.lessons || [];
        }
        catch (error) {
            console.error('Error fetching published lessons:', error);
            return [];
        }
    },
    // Increment view count
    async incrementViewCount(id) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Use the RPC function for secure update
            const { error } = await supabase.rpc('update_lesson_secure', {
                p_user_id: user.id,
                p_lesson_id: id,
                p_metadata: { view_count_incremented: true }
            });
            if (error)
                throw error;
        }
        catch (error) {
            console.error('Error incrementing view count:', error);
            throw error;
        }
    },
    // Rate a lesson
    async rateLesson(id, rating) {
        try {
            // Get current user ID
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Use the RPC function for secure update
            const { error } = await supabase.rpc('update_lesson_secure', {
                p_user_id: user.id,
                p_lesson_id: id,
                p_metadata: {
                    rating: rating,
                    rated_by: user.id,
                    rated_at: new Date().toISOString()
                }
            });
            if (error)
                throw error;
        }
        catch (error) {
            console.error('Error rating lesson:', error);
            throw error;
        }
    },
    // Test the lesson creation system
    async testLessonCreationSystem() {
        try {
            const { data, error } = await supabase.rpc('test_lesson_creation_system');
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error testing lesson creation system:', error);
            throw error;
        }
    },
    // Ensure tutor record exists
    async ensureTutorRecord() {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase.rpc('ensure_tutor_record', {
                p_user_id: user.id
            });
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error ensuring tutor record:', error);
            throw error;
        }
    }
};
